/* eslint-disable react-hooks/refs -- focus restoration intentionally passes DOM refs through the search boundary. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { HeightCollapseTransition } from 'site-modules/shared/components/inventory/global-search/height-collapse-transition/height-collapse-transition';
import {
  GLOBAL_NAV_CREATIVE_ID,
  GLOBAL_SEARCH_TRANSITION_DURATION,
  LISTBOX_ID,
  PRIMING_BOX_COPY,
} from 'site-modules/shared/constants/global-search/global-search';
import { delayHeavyTask } from 'site-modules/shared/utils/inp-utils';
import { GlobalSearch } from 'site-modules/shared/components/inventory/global-search/global-search';
import { IconMagicSearch } from 'site-modules/shared/components/icon-magic-search/icon-magic-search';
import { AiLink } from 'site-modules/shared/components/ai-link/ai-link';

import './global-navigation-search.scss';

const SEARCH_EDMUNDS_SEARCH = 'Search';

export function GlobalNavigationSearch({
  isMobile = false,
  withLlmSearchDisabled = false,
  withAiLink = true,
  use2027Theme = false,
}) {
  const [isOpen, setOpen] = useState(false);
  const inputRef = useRef();
  const buttonRef = useRef();

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      inputRef.current?.classList?.toggle('d-none', false);
      inputRef.current?.focus();
      setOpen(!isOpen);
    },
    [isOpen]
  );

  useEffect(() => {
    function toggleScrolling(disableScroll) {
      document.documentElement.classList.toggle('overflow-hidden', !!disableScroll);
    }

    if (!isMobile && isOpen) {
      delayHeavyTask(() => {
        toggleScrolling(true);
      });
    }

    return () => {
      delayHeavyTask(() => {
        toggleScrolling();
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      inputRef.current.classList.toggle('d-none', true);
    }
  }, [isOpen]);

  const globalSearch = (
    <GlobalSearch
      isMobile={isMobile}
      controlled
      isOpen={!isMobile || isOpen}
      onChangeOpenState={setOpen}
      isSearchVisible={isOpen}
      isGlobalNav
      withLlmSearchDisabled={withLlmSearchDisabled}
      closeOnResultSelect
      creativeId={GLOBAL_NAV_CREATIVE_ID}
      restoreFocusElement={buttonRef.current}
      formClassName="global-navigation-search-form"
      use2027Theme={use2027Theme}
    />
  );

  const searchBtn = (
    <Button
      className="pos-r p-0 border-0 bg-transparent d-flex align-items-center align-self-center"
      onClick={handleClick}
      innerRef={buttonRef}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-controls={LISTBOX_ID}
      aria-label={SEARCH_EDMUNDS_SEARCH}
    >
      <span className="d-inline-block">
        <IconMagicSearch
          classNames={classnames('search-icon size-24', {
            'text-white': !use2027Theme,
            'text-cool-gray-10': use2027Theme,
          })}
          iconClasses=""
        />
      </span>
    </Button>
  );

  return (
    <div
      className={classnames('global-navigation-search pos-r', {
        'w-100': !withAiLink,
        'd-flex justify-content-end': isMobile,
        'align-items-center ps-0_75': isMobile && withAiLink,
        'ps-1_5': !(isMobile && withAiLink),
        'ps-0': !isMobile && withAiLink,
        'pe-1_5': !isMobile,
        'pe-0': !isMobile && withAiLink,
      })}
    >
      {withAiLink && (
        <div className="pos-r w-100 d-flex justify-content-center">
          <AiLink linkClasses="me-1_5" />
          {searchBtn}
        </div>
      )}

      {/* ios hack to open keyboard in drawer */}
      <input aria-hidden type="text" ref={inputRef} className="visually-hidden" tabIndex={-1} />

      {!isMobile && (
        <HeightCollapseTransition isOpen={isOpen} timeout={GLOBAL_SEARCH_TRANSITION_DURATION}>
          <div
            id={LISTBOX_ID}
            role="dialog"
            aria-label={PRIMING_BOX_COPY}
            className="global-nav-search-collapse"
            style={{ position: 'fixed', zIndex: 1 }}
          >
            {globalSearch}
          </div>
        </HeightCollapseTransition>
      )}
      {isMobile && globalSearch}
    </div>
  );
}

GlobalNavigationSearch.propTypes = {
  isMobile: PropTypes.bool,
  withLlmSearchDisabled: PropTypes.bool,
  withAiLink: PropTypes.bool,
};
